import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, Eye, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { teamMembers, milestones } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Story</h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            Born from compassion, driven by purpose, sustained by a community that believes in dignity for all.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-2 border-[#2D5F3F] shadow-lg">
              <CardContent className="p-10">
                <div className="bg-[#2D5F3F] w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#2D5F3F] mb-4">Our Mission</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  To collect, refurbish, and deliver life-changing mobility and assistive equipment from the UK to vulnerable individuals in Nigeria, restoring dignity, independence, and hope.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#D4AF37] shadow-lg">
              <CardContent className="p-10">
                <div className="bg-[#D4AF37] w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#2D5F3F] mb-4">Our Vision</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  A world where no person is denied mobility and independence due to lack of resources. Where compassion crosses borders and every life is valued with equal dignity.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">How It Began</h2>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-l-8 border-[#D4AF37]">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                In 2015, Rev. James Adebayo returned from a ministry visit to his home state in Nigeria and couldn't shake what he'd witnessed: people with profound mobility needs crawling on bare ground, isolated in their homes, denied dignity and participation in community life. Not because solutions didn't exist—but because resources hadn't reached them.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Meanwhile, across the UK, thousands of wheelchairs, walking frames, and mobility aids were being discarded or stored unused after loved ones passed or recovered. Good equipment. Life-changing equipment. Just waiting for someone to bridge the gap.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                From a small Birmingham church hall, Bridge of Hope Foundation was born. What started as informal collections grew into a registered charity with collection hubs, refurbishment workshops, partnership networks, and systematic container shipping operations.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Today, we've shipped over 45 containers, transformed 2,500+ lives, and built a movement of donors, volunteers, partners, and trustees who believe that compassion should never stop at borders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Our Values</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Heart, title: "Compassion", desc: "We see every person as worthy of dignity, care, and respect" },
              { icon: Award, title: "Integrity", desc: "Transparent finances, accountable governance, ethical operations" },
              { icon: Target, title: "Excellence", desc: "High standards in equipment quality, delivery, and stewardship" },
              { icon: Eye, title: "Partnership", desc: "Working with communities, not imposing from outside" }
            ].map((value, index) => (
              <Card key={index} className="text-center border-2 border-gray-100 hover:border-[#2D5F3F] transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] rounded-2xl mb-4">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2D5F3F] mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Our Journey</h2>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#D4AF37]"></div>
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative pl-20">
                  <div className="absolute left-5 top-0 w-7 h-7 bg-[#D4AF37] rounded-full border-4 border-white shadow-lg"></div>
                  <Card className="bg-white shadow-md hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="text-2xl font-bold text-[#D4AF37] mb-2">{milestone.year}</div>
                      <p className="text-gray-700 text-lg">{milestone.event}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Our Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced, committed, and passionate about creating lasting impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="relative h-64 overflow-hidden rounded-t-lg">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#2D5F3F] mb-1">{member.name}</h3>
                  <p className="text-sm text-[#D4AF37] font-semibold mb-3">{member.role}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Join Our Story</h2>
          <p className="text-xl mb-8 text-gray-200">
            Be part of a movement that's transforming lives across borders. Your time, gifts, and partnership matter.
          </p>
          <Link to="/get-involved">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#C19B2C] text-white font-semibold px-10 py-6 text-lg shadow-xl transition-all duration-300 hover:scale-105">
              Get Involved Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
